<template>
    <div class="h-screen flex flex-row">
        <!-- Левая панель для управления маршрутами -->
        <div class="w-96 bg-white shadow-xl flex flex-col z-20 h-4/5">
            <!-- Заголовок панели -->
            <div class="p-4 border-b bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                <h2 class="text-xl font-semibold">🗺️ Морской маршрут</h2>
                <p class="text-sm opacity-90 mt-1">Добавляйте точки кликами по карте</p>
            </div>

            <!-- Список точек маршрута -->
            <div class="flex-1 overflow-y-auto p-4">
                <div class="space-y-3">
                    <!-- Кнопка добавления точки -->
                    <button @click="addEmptyPoint"
                        class="w-full py-2 border-2 border-dashed border-blue-300 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4">
                            </path>
                        </svg>
                        Добавить точку
                    </button>

                    <!-- Список всех точек -->
                    <div v-for="(point, index) in routePoints" :key="index"
                        class="bg-gray-50 rounded-lg p-3 hover:shadow-md transition-shadow"
                        :class="{ 'border-2 border-green-500': index === 0, 'border-2 border-red-500': index === routePoints.length - 1 && routePoints.length > 1 }">
                        <div class="flex items-center justify-between mb-2">
                            <div class="flex items-center gap-2">
                                <div class="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                    :class="{
                                        'bg-green-500': index === 0,
                                        'bg-red-500': index === routePoints.length - 1 && routePoints.length > 1,
                                        'bg-blue-500': index !== 0 && index !== routePoints.length - 1
                                    }">
                                    {{ index + 1 }}
                                </div>
                                <span class="text-sm font-medium text-gray-700">
                                    {{ getPointType(index) }}
                                </span>
                            </div>
                            <div class="flex gap-1">
                                <button @click="editPointCoordinates(index)"
                                    class="text-gray-400 hover:text-blue-500 transition-colors"
                                    title="Редактировать координаты">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z">
                                        </path>
                                    </svg>
                                </button>
                                <button v-if="routePoints.length > 2 && index !== 0 && index !== routePoints.length - 1"
                                    @click="removeRoutePoint(index)"
                                    class="text-gray-400 hover:text-red-500 transition-colors" title="Удалить точку">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                                        </path>
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div class="text-sm font-mono text-gray-600">
                            {{ formatCoordinatesForDisplay(point) }}
                        </div>

                        <div v-if="index < routePoints.length - 1"
                            class="mt-2 text-xs text-gray-400 flex items-center gap-1">
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                            </svg>
                            {{ getSegmentDistance(index).toFixed(1) }} км
                        </div>
                    </div>

                    <div v-if="routePoints.length === 0" class="text-center py-8 text-gray-400">
                        <svg class="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7">
                            </path>
                        </svg>
                        <p>Кликните на карту<br>чтобы добавить точки маршрута</p>
                    </div>
                </div>
            </div>

            <!-- Итоговая информация о маршруте -->
            <div class="border-t bg-gray-50 p-4 space-y-3">
                <div class="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-3 text-white">
                    <div class="text-sm opacity-90">Общее расстояние</div>
                    <div class="text-2xl font-bold">{{ totalRouteDistance }} км</div>
                    <div class="text-xs opacity-75 mt-1">
                        {{ routePoints.length }} {{ getPointsWord(routePoints.length) }} •
                        {{ routeSegments.length }} {{ getSegmentsWord(routeSegments.length) }}
                    </div>
                </div>

                <div v-if="routeSegments.length > 0" class="text-sm">
                    <div class="font-medium text-gray-700 mb-2">📊 Сегменты маршрута:</div>
                    <div class="space-y-1 max-h-32 overflow-y-auto">
                        <div v-for="(segment, idx) in routeSegments" :key="idx"
                            class="flex justify-between text-gray-600">
                            <span>От {{ idx + 1 }} до {{ idx + 2 }}</span>
                            <span class="font-mono">{{ segment.distance.toFixed(1) }} км</span>
                        </div>
                    </div>
                </div>

                <div class="flex gap-2">
                    <button @click="centerOnRoute"
                        class="flex-1 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
                        :disabled="routePoints.length === 0">
                        🎯 Центрировать
                    </button>
                    <button @click="clearAllPoints"
                        class="flex-1 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-sm"
                        :disabled="routePoints.length === 0">
                        🗑️ Очистить
                    </button>
                </div>
                <button @click="exportRoute"
                    class="w-full py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors text-sm"
                    :disabled="routePoints.length === 0">
                    💾 Экспорт маршрута (JSON)
                </button>
            </div>
        </div>

        <!-- Основная область с картой -->
        <div class="flex-1 relative h-4/5">
            <div ref="mapContainer" class="absolute inset-0"></div>

            <div v-if="routePoints.length > 0"
                class="absolute top-4 right-4 z-10 bg-white rounded-full shadow-md px-3 py-1 text-sm font-medium text-gray-700">
                📍 {{ routePoints.length }} {{ getPointsWord(routePoints.length) }} | {{ totalRouteDistance }} км
            </div>
        </div>

        <!-- Модальное окно для редактирования координат -->
        <div v-if="isEditModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30">
            <div class="bg-white rounded-lg shadow-xl w-96 p-6">
                <h3 class="text-lg font-semibold mb-4">✏️ Редактировать координаты</h3>
                <div class="space-y-3">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Широта (Latitude)</label>
                        <input v-model.number="editLat" type="number" step="0.0001"
                            class="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono" />
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Долгота (Longitude)</label>
                        <input v-model.number="editLon" type="number" step="0.0001"
                            class="w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 font-mono" />
                    </div>
                </div>
                <div class="flex justify-end gap-2 mt-6">
                    <button @click="closeEditModal"
                        class="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md">Отмена</button>
                    <button @click="saveEditedPoint"
                        class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600">Сохранить</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';

// Состояние карты
const mapContainer = ref(null);
let map = null;
let vectorSource = null;

// Точки маршрута (единый источник истины)
const routePoints = ref([]);

// Состояние модального окна редактирования
const isEditModalOpen = ref(false);
const editIndex = ref(null);
const editLat = ref(0);
const editLon = ref(0);

// Форматирование координат
const formatCoordinatesForDisplay = (coords) => {
    if (!coords) return '';
    const lat = Math.abs(coords[1]).toFixed(4);
    const lon = Math.abs(coords[0]).toFixed(4);
    const latDir = coords[1] >= 0 ? 'N' : 'S';
    const lonDir = coords[0] >= 0 ? 'E' : 'W';
    return `${lat}° ${latDir}, ${lon}° ${lonDir}`;
};

// Тип точки
const getPointType = (index) => {
    if (index === 0) return '🚢 Отправление';
    if (index === routePoints.value.length - 1 && routePoints.value.length > 1) return '🏁 Назначение';
    return '⚓ Промежуточная';
};

// Склонение слов
const getPointsWord = (count) => {
    if (count % 10 === 1 && count % 100 !== 11) return 'точка';
    if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'точки';
    return 'точек';
};

const getSegmentsWord = (count) => {
    if (count % 10 === 1 && count % 100 !== 11) return 'сегмент';
    if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'сегмента';
    return 'сегментов';
};

// Расчет расстояния между двумя точками
const calculateDistanceBetweenPoints = (point1, point2) => {
    if (!point1 || !point2) return 0;

    const R = 6371;
    const lat1 = point1[1] * Math.PI / 180;
    const lat2 = point2[1] * Math.PI / 180;
    const dLat = (point2[1] - point1[1]) * Math.PI / 180;
    const dLon = (point2[0] - point1[0]) * Math.PI / 180;

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
};

// Получение расстояния для сегмента
const getSegmentDistance = (index) => {
    if (index >= routePoints.value.length - 1) return 0;
    return calculateDistanceBetweenPoints(routePoints.value[index], routePoints.value[index + 1]);
};

// Сегменты маршрута
const routeSegments = computed(() => {
    const segments = [];
    for (let i = 0; i < routePoints.value.length - 1; i++) {
        segments.push({
            from: routePoints.value[i],
            to: routePoints.value[i + 1],
            distance: calculateDistanceBetweenPoints(routePoints.value[i], routePoints.value[i + 1])
        });
    }
    return segments;
});

// Общее расстояние
const totalRouteDistance = computed(() => {
    const total = routeSegments.value.reduce((sum, segment) => sum + segment.distance, 0);
    return total.toFixed(1);
});

// Добавление точки в центре карты
const addEmptyPoint = () => {
    if (map) {
        const view = map.getView();
        const center = view.getCenter();
        const lonLat = toLonLat(center);
        routePoints.value.push(lonLat);
        updateMapFeatures();
    } else {
        routePoints.value.push([131.8869, 43.1151]);
        updateMapFeatures();
    }
};

// Удаление точки
const removeRoutePoint = (index) => {
    routePoints.value.splice(index, 1);
    updateMapFeatures();
};

// Редактирование координат
const editPointCoordinates = (index) => {
    editIndex.value = index;
    editLat.value = routePoints.value[index][1];
    editLon.value = routePoints.value[index][0];
    isEditModalOpen.value = true;
};

const closeEditModal = () => {
    isEditModalOpen.value = false;
    editIndex.value = null;
};

const saveEditedPoint = () => {
    if (editIndex.value !== null) {
        routePoints.value[editIndex.value] = [editLon.value, editLat.value];
        updateMapFeatures();
        closeEditModal();
    }
};

// Центрирование на маршруте
const centerOnRoute = () => {
    if (routePoints.value.length === 0 || !map) return;

    let minLon = Infinity, maxLon = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;

    routePoints.value.forEach(point => {
        minLon = Math.min(minLon, point[0]);
        maxLon = Math.max(maxLon, point[0]);
        minLat = Math.min(minLat, point[1]);
        maxLat = Math.max(maxLat, point[1]);
    });

    const centerLon = (minLon + maxLon) / 2;
    const centerLat = (minLat + maxLat) / 2;
    const center = fromLonLat([centerLon, centerLat]);

    map.getView().setCenter(center);

    const lonDiff = maxLon - minLon;
    const latDiff = maxLat - minLat;
    const maxDiff = Math.max(lonDiff, latDiff);

    let zoom = 10;
    if (maxDiff < 0.01) zoom = 14;
    else if (maxDiff < 0.05) zoom = 12;
    else if (maxDiff < 0.2) zoom = 10;
    else if (maxDiff < 0.5) zoom = 8;
    else if (maxDiff < 1) zoom = 7;
    else zoom = 6;

    map.getView().setZoom(zoom);
};

// Очистка всех точек
const clearAllPoints = () => {
    routePoints.value = [];
    if (vectorSource) {
        vectorSource.clear();
    }
};

// Экспорт маршрута
const exportRoute = () => {
    const routeData = {
        name: `Морской маршрут от ${new Date().toLocaleDateString()}`,
        points: routePoints.value.map((point, idx) => ({
            order: idx + 1,
            type: idx === 0 ? 'start' : (idx === routePoints.value.length - 1 ? 'end' : 'waypoint'),
            lat: point[1],
            lon: point[0],
            lat_formatted: formatCoordinatesForDisplay(point)
        })),
        segments: routeSegments.value.map((segment, idx) => ({
            from: idx + 1,
            to: idx + 2,
            distance_km: segment.distance.toFixed(1)
        })),
        total_distance_km: totalRouteDistance.value,
        total_points: routePoints.value.length,
        export_date: new Date().toISOString()
    };

    const dataStr = JSON.stringify(routeData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', `marine_route_${new Date().toISOString().slice(0, 19)}.json`);
    linkElement.click();
};

// Обновление карты
const updateMapFeatures = () => {
    if (!vectorSource) return;
    vectorSource.clear();

    // Добавляем точки
    routePoints.value.forEach((point, index) => {
        const geometry = new Point(fromLonLat(point));
        let color;
        if (index === 0) color = '#00ff00';
        else if (index === routePoints.value.length - 1 && routePoints.value.length > 1) color = '#ff0000';
        else color = '#0066ff';

        const feature = new Feature({ geometry });
        feature.setStyle(new Style({
            image: new CircleStyle({
                radius: 8,
                fill: new Fill({ color }),
                stroke: new Stroke({ color: '#ffffff', width: 2 })
            })
        }));
        vectorSource.addFeature(feature);
    });

    // Добавляем линию маршрута
    if (routePoints.value.length >= 2) {
        const routeGeometry = new LineString(
            routePoints.value.map(point => fromLonLat(point))
        );
        const routeFeature = new Feature({ geometry: routeGeometry });
        routeFeature.setStyle(new Style({
            stroke: new Stroke({
                color: '#0066ff',
                width: 4,
                lineDash: [10, 8]
            })
        }));
        vectorSource.addFeature(routeFeature);
    }
};

// Обработчик клика по карте
const handleMapClick = (event) => {
    const coordinate = event.coordinate;
    const lonLat = toLonLat(coordinate);
    routePoints.value.push(lonLat);
    updateMapFeatures();
};

// Инициализация карты
onMounted(() => {
    vectorSource = new VectorSource();

    const bathymetryLayer = new TileLayer({
        source: new XYZ({
            url: 'https://services.arcgisonline.com/arcgis/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}',
            attribution: '&copy; NOAA'
        }),
        opacity: 0.6
    });

    map = new Map({
        target: mapContainer.value,
        layers: [
            new TileLayer({ source: new OSM() }),
            new TileLayer({
                source: new XYZ({
                    url: 'https://seataxi.online/api/map-proxy/seamark/{z}/{x}/{y}.png'
                })
            }),
            //bathymetryLayer,
            new VectorLayer({ source: vectorSource })
        ],
        view: new View({
            center: fromLonLat([131.8869, 43.1151]),
            zoom: 12,
            projection: 'EPSG:3857'
        })
    });

    map.on('click', handleMapClick);
});

onBeforeUnmount(() => {
    if (map) {
        map.setTarget(null);
        map = null;
    }
});
</script>

<style scoped>
.overflow-y-auto {
    scrollbar-width: thin;
}

.overflow-y-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #555;
}

button:focus {
    outline: none;
}
</style>